<template>
  <div class="bg-white dark:bg-[#0c1427] p-5 rounded-md shadow-md flex flex-col min-h-[450px]">
    <h6 class="text-black dark:text-white font-semibold mb-6 text-center text-xl">{{ directT('settings.customersSettings.customFields.management') }}</h6>
    
    <div class="mb-5 flex-grow">
      <div v-if="fields.length === 0" class="text-gray-500 italic">
        {{ directT('settings.customersSettings.customFields.noFields') }}
      </div>
      
      <ul class="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
        <li 
          v-for="(field, index) in fields" 
          :key="index"
          class="flex items-center justify-between bg-white dark:bg-[#0c1427] p-3 rounded border border-gray-200 dark:border-[#1c2b50]"
        >
          <div class="flex items-center">
            <span class="text-black dark:text-white">{{ field.field }}</span>
            <span 
              v-if="field.isrequired" 
              class="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded"
            >
              Required
            </span>
          </div>
          <div class="flex items-center gap-2">
            <button 
              @click="editField(index)" 
              class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <i class="material-symbols-outlined !text-md">edit</i>
            </button>
            <button 
              @click="confirmDeleteField(index)" 
              class="text-red-500 hover:text-red-700"
            >
              <i class="material-symbols-outlined !text-md">delete</i>
            </button>
          </div>
        </li>
      </ul>
    </div>
    
    <div class="flex justify-between flex-nowrap gap-2 w-full mt-auto">
      <button 
        @click="addNewField" 
        class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md flex items-center justify-center whitespace-nowrap min-w-[120px]"
      >
        <span>{{ directT('settings.customersSettings.customFields.addField') }}</span>
      </button>
      
      <button 
        @click="saveFields" 
        class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md whitespace-nowrap min-w-[120px] flex items-center justify-center"
        :disabled="saving"
      >
        {{ saving ? directT('settings.customersSettings.customFields.saving') : directT('settings.customersSettings.customFields.saveChanges') }}
      </button>
    </div>
    <p class="text-sm text-red-500 mt-2 text-center">
      {{ directT('settings.customersSettings.customFields.important') }}
    </p>
    
    <!-- Field Modal -->
    <div 
      v-if="showFieldModal" 
      class="fixed inset-0 flex items-center justify-center z-50"
    >
      <div class="fixed inset-0 bg-black opacity-50" @click="closeFieldModal"></div>
      <div class="bg-white dark:bg-[#0c1427] p-6 rounded-md shadow-lg w-full max-w-md relative z-10">
        <h5 class="text-xl font-bold mb-4 text-black dark:text-white">
          {{ editingFieldIndex === -1 ? directT('settings.customersSettings.customFields.addCustomFieldTitle') : directT('settings.customersSettings.customFields.editCustomFieldTitle') }}
        </h5>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ directT('settings.customersSettings.customFields.fieldName') }}
          </label>
          <input
            v-model="editingField.field"
            type="text"
            class="h-[45px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-4 block w-full outline-0 focus:border-primary-500"
            :placeholder="directT('settings.customersSettings.customFields.fieldNamePlaceholder')"
          />
        </div>
        
        <div class="mb-6">
          <label class="flex items-center space-x-2 cursor-pointer">
            <input
              v-model="editingField.isrequired"
              type="checkbox"
              class="form-checkbox h-5 w-5 text-primary-500 rounded border-gray-300 focus:ring-primary-500"
            />
            <span class="text-gray-700 dark:text-gray-300">{{ directT('settings.customersSettings.customFields.requiredField') }}</span>
          </label>
        </div>
        
        <div class="flex justify-end space-x-3">
          <button
            @click="closeFieldModal"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-50 dark:hover:bg-[#172036]"
          >
            {{ directT('settings.customersSettings.statusManagement.cancel') }}
          </button>
          <button
            @click="saveField"
            class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md"
          >
            {{ editingFieldIndex === -1 ? directT('settings.customersSettings.statusManagement.add') : directT('settings.customersSettings.statusManagement.update') }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div 
      v-if="showDeleteModal" 
      class="fixed inset-0 flex items-center justify-center z-50"
    >
      <div class="fixed inset-0 bg-black opacity-50" @click="closeDeleteModal"></div>
      <div class="bg-white dark:bg-[#0c1427] p-6 rounded-md shadow-lg w-full max-w-md relative z-10">
        <h5 class="text-xl font-bold mb-4 text-black dark:text-white">{{ directT('settings.customersSettings.statusManagement.confirmDeleteTitle') }}</h5>
        <p class="mb-6 text-gray-700 dark:text-gray-300">
          {{ directT('settings.customersSettings.customFields.deleteFieldConfirmation') }}
        </p>
        
        <div class="flex justify-end space-x-3">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-50 dark:hover:bg-[#172036]"
          >
            {{ directT('settings.customersSettings.statusManagement.cancel') }}
          </button>
          <button
            @click="deleteField"
            class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
          >
            {{ directT('settings.customersSettings.statusManagement.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, PropType } from "vue";
import { supabase } from "@/lib/supabaseClient";
import { createToast } from "@/utils/toast";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

interface CustomField {
  id?: string;
  organizationid?: string;
  field: string;
  isrequired: boolean;
  created_at?: string;
  updated_at?: string;
}

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "CustomFields",
  props: {
    organizationId: {
      type: String as PropType<string | null>,
      default: null
    }
  },
  emits: ['field-saved', 'field-deleted'],
  setup(props, { emit }) {
    const fields = ref<CustomField[]>([]);
    const saving = ref(false);
    const currentOrgId = ref<string | null>(null);
    
    // Field modal
    const showFieldModal = ref(false);
    const editingField = ref<CustomField>({ field: '', isrequired: false });
    const editingFieldIndex = ref(-1);
    
    // Delete confirmation modal
    const showDeleteModal = ref(false);
    const deleteIndex = ref<number>(-1);
    
    // i18n setup
    const { locale } = useI18n();
    
    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };
    
    // Fetch organization ID if not provided
    onMounted(async () => {
      try {
        if (props.organizationId) {
          currentOrgId.value = props.organizationId;
        } else {
          currentOrgId.value = await getCurrentOrgId();
        }
        
        if (!currentOrgId.value) {
          createToast(directT('settings.customersSettings.toastMessages.missingOrg'), 'error');
          return;
        }
        
        await fetchCustomFields();
      } catch (error) {
        console.error('Error loading custom fields:', error);
        createToast(directT('settings.customersSettings.toastMessages.loadingError'), 'error');
      }
    });
    
    // Watch for changes in the organizationId prop
    watch(() => props.organizationId, (newValue) => {
      if (newValue) {
        currentOrgId.value = newValue;
        fetchCustomFields();
      }
    });
    
    // Fetch custom fields from the database
    const fetchCustomFields = async () => {
      if (!currentOrgId.value) return;
      
      try {
        const { data, error } = await supabase
          .from('customerscustomfields')
          .select('*')
          .eq('organizationid', currentOrgId.value);
        
        if (error) throw error;
        
        fields.value = (data || []).map((item: any) => ({
          id: item.id,
          organizationid: item.organizationid,
          field: item.field,
          isrequired: item.isrequired,
          created_at: item.created_at,
          updated_at: item.updated_at
        }));
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        createToast(directT('settings.customersSettings.toastMessages.fetchError'), 'error');
      }
    };
    
    // Open the field modal for adding a new field
    const addNewField = () => {
      editingField.value = { field: '', isrequired: false };
      editingFieldIndex.value = -1;
      showFieldModal.value = true;
    };
    
    // Open the field modal for editing an existing field
    const editField = (index: number) => {
      const field = fields.value[index];
      editingField.value = { ...field };
      editingFieldIndex.value = index;
      showFieldModal.value = true;
    };
    
    // Save the current field being edited
    const saveField = () => {
      // Validate inputs
      if (!editingField.value.field.trim()) {
        createToast(directT('settings.customersSettings.toastMessages.fieldRequired'), 'error');
        return;
      }
      
      // Check for duplicate field names
      const isDuplicate = fields.value.some((field, index) => 
        field.field.toLowerCase() === editingField.value.field.toLowerCase() &&
        index !== editingFieldIndex.value
      );
      
      if (isDuplicate) {
        createToast(directT('settings.customersSettings.toastMessages.duplicateField'), 'error');
        return;
      }
      
      // Update the fields list with the new or edited field
      if (editingFieldIndex.value === -1) {
        // Add new field
        fields.value.push({ ...editingField.value });
      } else {
        // Update existing field
        fields.value[editingFieldIndex.value] = { ...editingField.value };
      }
      
      // Emit event
      emit('field-saved', editingField.value);
      
      // Close the modal
      closeFieldModal();
    };
    
    // Close the field modal
    const closeFieldModal = () => {
      showFieldModal.value = false;
      editingField.value = { field: '', isrequired: false };
      editingFieldIndex.value = -1;
    };
    
    // Confirm deletion of a field
    const confirmDeleteField = (index: number) => {
      deleteIndex.value = index;
      showDeleteModal.value = true;
    };
    
    // Delete a field
    const deleteField = () => {
      if (deleteIndex.value >= 0 && deleteIndex.value < fields.value.length) {
        const fieldToDelete = fields.value[deleteIndex.value];
        fields.value.splice(deleteIndex.value, 1);
        
        // Emit event
        if (fieldToDelete.id) {
          emit('field-deleted', fieldToDelete.id);
        }
      }
      closeDeleteModal();
    };
    
    // Close the delete modal
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      deleteIndex.value = -1;
    };
    
    // Save all fields to the database
    const saveFields = async () => {
      if (!currentOrgId.value) {
        createToast(directT('settings.customersSettings.toastMessages.missingOrg'), 'error');
        return;
      }
      
      saving.value = true;
      console.log('Saving custom fields to database:', fields.value, 'organizationId:', currentOrgId.value);
      
      try {
        // Get existing fields from the database
        const { data: existingFieldsData, error: fetchError } = await supabase
          .from('customerscustomfields')
          .select('id')
          .eq('organizationid', currentOrgId.value);
        
        if (fetchError) throw fetchError;
        
        // Create a set of existing IDs
        const existingIds = new Set((existingFieldsData || []).map((item: any) => item.id));
        
        // Identify fields to add, update, or delete
        const fieldsToUpdate: any[] = [];
        const fieldsToAdd: any[] = [];
        
        // Process fields in the UI
        fields.value.forEach((field) => {
          const dbField = {
            field: field.field,
            isrequired: field.isrequired,
            organizationid: currentOrgId.value as string
          };
          
          if (field.id && existingIds.has(field.id)) {
            // Field exists, update it
            fieldsToUpdate.push({
              ...dbField,
              id: field.id
            });
            // Remove from the set of existing IDs to track what's left to delete
            existingIds.delete(field.id);
          } else {
            // Field is new, add it
            fieldsToAdd.push(dbField);
          }
        });
        
        // Update existing fields
        if (fieldsToUpdate.length > 0) {
          const { error: updateError } = await supabase
            .from('customerscustomfields')
            .upsert(fieldsToUpdate);
          
          if (updateError) throw updateError;
        }
        
        // Add new fields
        if (fieldsToAdd.length > 0) {
          const { error: addError } = await supabase
            .from('customerscustomfields')
            .insert(fieldsToAdd);
          
          if (addError) throw addError;
        }
        
        // Delete removed fields
        if (existingIds.size > 0) {
          const { error: deleteError } = await supabase
            .from('customerscustomfields')
            .delete()
            .in('id', Array.from(existingIds));
          
          if (deleteError) throw deleteError;
        }
        
        // Refresh the fields list
        await fetchCustomFields();
        
        createToast(directT('settings.customersSettings.toastMessages.saveSuccess'), 'success');
      } catch (error) {
        console.error('Error saving custom fields:', error);
        createToast(directT('settings.customersSettings.toastMessages.saveError'), 'error');
      } finally {
        saving.value = false;
      }
    };
    
    return {
      fields,
      saving,
      showFieldModal,
      editingField,
      editingFieldIndex,
      showDeleteModal,
      deleteIndex,
      directT,
      addNewField,
      editField,
      confirmDeleteField,
      closeFieldModal,
      closeDeleteModal,
      saveField,
      deleteField,
      saveFields
    };
  }
});
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: #0c1427;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #555;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #777;
}
</style> 